import { computed } from "vue";
import { useAuth0 } from "@auth0/auth0-vue";
import { getTimeZoneOffsetInUTC } from "~/utils/getTimeZoneOffsetInUTC.ts";
import { useLogout } from "~/composables/useLogout.ts";
import { useUpNavRoutes } from "~/composables/useUpNavRoutes.ts";
import { useIntercomHelp } from "~/composables/useIntercomHelp.ts";
import { useOrgStore } from "~/store/org";
import { useUserStore } from "~/store/user";
import { FEATURE_FLAGS } from "~/constants/featureFlags.ts";
import type { Route, SidebarOptionName } from "~/types/UpNavRoutesType.ts";
import type Emitter from "~/types/Emitter";
import { USER_APPLICATIONS } from "~/constants/userApplications.ts";
import { upNavRoutePaths } from "~/constants/upNavRouteConfig.ts";

export const useUpNavigationStore = defineStore("upNavigation", () => {
  const auth0 = useAuth0();
  const { UP_NAVIGATION_FLAG, NEW_ANALYTICS_FLAG } = FEATURE_FLAGS;
  const { UNIFIED_APPLICATION, PRM_APPLICATION, REVIEW_APPLICATION } =
    USER_APPLICATIONS;
  const { $bus } = useNuxtApp() as unknown as { $bus: Emitter };

  // ------------------ Composables ------------------

  const { toggleIntercomHelp } = useIntercomHelp();
  const {
    upNavRoutesById,
    upNavSidebarOptions,
    upNavNavbarOptions,
    upNavbarDropdownOptions,
    upNavRoutesByPath,
  } = useUpNavRoutes();

  // ------------------ Stores ------------------

  const userStore = useUserStore();
  const orgStore = useOrgStore();

  // ------------------ State ------------------
  const isNavAvatarMenuVisible = ref<boolean>(false);
  const isNavbarVisible = ref<boolean>(false);
  const isNavDropdownVisible = ref<boolean>(false);
  const activeRoute = ref<Route | null>();
  const activeSidebarOption = ref<Route | null>();
  const navBarOptions = ref<Route[]>();
  const activeNavBarOption = ref<Route | null>();
  const navBarDropdownOptions = ref<Route[]>();
  const activeNavBarDropdownOption = ref<Route | null>();
  const activeTabOption = ref<Route | null>();
  const navSidebarOptions = [...upNavSidebarOptions.values()];

  // ------------------ Getters ------------------

  const hasMonitorApplicationAccess = computed(() => {
    return userStore.hasApplicationAccess(UNIFIED_APPLICATION);
  });

  const hasContactApplicationAccess = computed(() => {
    return userStore.hasApplicationAccess(PRM_APPLICATION);
  });

  const hasReviewApplicationAccess = computed(() => {
    return userStore.hasApplicationAccess(REVIEW_APPLICATION);
  });

  const avatarMenuOptions = computed(() => {
    return [...upNavSidebarOptions.values()].filter(
      (option) => !option.isPrefix,
    );
  });

  const avatarMenuTimeZone = computed(() => {
    const timeZone = orgStore.getTimeZone ?? "";
    const timeZoneOffset = getTimeZoneOffsetInUTC(timeZone);
    const timeZoneWithOffset = `${timeZone} (UTC${timeZoneOffset})`;
    return timeZoneOffset ? timeZoneWithOffset : timeZone;
  });

  const isSidebarOptionPremiumBadgeVisible = computed<
    Partial<Record<SidebarOptionName, boolean>>
  >(() => {
    return {
      Monitor: !hasMonitorApplicationAccess.value,
      Contact: !hasContactApplicationAccess.value,
      Review: !hasReviewApplicationAccess.value,
    };
  });

  const isAnalyticsVisible = computed(() => {
    return Boolean(newAnalyticsFlag.value);
  });

  const visibleNavSidebarOptions = computed(() => {
    return navSidebarOptions.filter((option) => {
      if (option.name === "Analytics") {
        return isAnalyticsVisible.value;
      }
      return true;
    });
  });

  const isAvatarMenuOptionActive = computed(() => (option: Route) => {
    return activeSidebarOption.value?.name === option?.sidebarOptionName;
  });

  // ------------------ Actions ------------------

  const {
    state: upNavFlag,
    isReady: isUpNavFlagReady,
    isLoading: isUpNavFlagLoading,
    executeImmediate: fetchUpNavFlag,
  } = useAsyncState<boolean>(async () => {
    const { useLaunchDarkly } = await import("~/composables/useLaunchDarkly");
    const { getFlagValue } = await useLaunchDarkly();
    return await getFlagValue(UP_NAVIGATION_FLAG);
  }, false);

  const {
    state: newAnalyticsFlag,
    isReady: isNewAnalyticsFlagReady,
    isLoading: isNewAnalyticsFlagLoading,
    executeImmediate: fetchNewAnalyticsFlag,
  } = useAsyncState<boolean>(async () => {
    const { useLaunchDarkly } = await import("~/composables/useLaunchDarkly");
    const { getFlagValue } = await useLaunchDarkly();
    return await getFlagValue(NEW_ANALYTICS_FLAG);
  }, false);

  const setActiveRoute = (route: Route | null) => {
    activeRoute.value = route;
  };

  const setActiveSideBarOption = (sidebarOptionName: SidebarOptionName) => {
    activeSidebarOption.value = upNavSidebarOptions.get(sidebarOptionName);
  };

  const toggleNavAvatarMenuVisibility = () => {
    isNavAvatarMenuVisible.value = !isNavAvatarMenuVisible.value;
  };

  const setNavbarVisibility = (value: boolean) => {
    isNavbarVisible.value = value;
  };

  const setNavAvatarMenuVisibility = (value: boolean) => {
    isNavAvatarMenuVisible.value = value;
  };

  const setNavBarOptions = (sidebarOptionName: SidebarOptionName) => {
    navBarOptions.value = upNavNavbarOptions.get(sidebarOptionName);
  };

  const setActiveNavBarOption = (navbarOptionId: number) => {
    activeNavBarOption.value = upNavRoutesById.get(navbarOptionId);
  };

  const setNavDropdownVisibility = (value: boolean) => {
    isNavDropdownVisible.value = value;
  };

  const hasDropdownOptions = (navbarOptionId: number) => {
    return upNavbarDropdownOptions.has(navbarOptionId);
  };

  const setNavBarDropdownOptions = (navbarOptionId: number) => {
    const dropdownOptions = upNavbarDropdownOptions.get(navbarOptionId);

    if (!dropdownOptions) {
      navBarDropdownOptions.value = undefined;
      return;
    }

    navBarDropdownOptions.value = dropdownOptions.filter((option) => {
      if (
        option.path === upNavRoutePaths.monitorSocial.path &&
        !orgStore.hasSocialMediaAccess
      ) {
        return false;
      }

      return true;
    });
  };

  const setActiveNavBarDropdownOption = (navbarDropdownOptionId: number) => {
    activeNavBarDropdownOption.value = upNavRoutesById.get(
      navbarDropdownOptionId,
    );
  };

  const setActiveTabOption = (tabOptionId: number) => {
    activeTabOption.value = upNavRoutesById.get(tabOptionId);
  };

  const handleSidebarOptionClick = async (option: Route) => {
    if (option.sidebarOptionName === "Contact") {
      if (hasContactApplicationAccess.value) {
        return navigateTo(useRuntimeConfig().public.PRM_URL, {
          external: true,
        });
      } else {
        return navigateTo(option.upsellPath);
      }
    }

    if (option.sidebarOptionName === "Review") {
      return navigateTo(useRuntimeConfig().public.REVIEW_URL, {
        external: true,
      });
    }

    if (option.name === "Help") {
      toggleIntercomHelp();
      return;
    }

    if (option.name === "Logout") {
      await userStore.endUserImpersonation();
      await useLogout(auth0);
      return;
    }

    return navigateTo(option.path, { external: option.isExternalRoute });
  };

  const handleAvatarMenuOptionClick = async (option: Route) => {
    if (option.name === "Settings") {
      await navigateTo(option.path);
      setNavAvatarMenuVisibility(false);
      return;
    }
    if (option.name === "Help") {
      toggleIntercomHelp();
      setNavAvatarMenuVisibility(false);
      return;
    }
    if (option.name === "Logout") {
      await userStore.endUserImpersonation();
      await useLogout(auth0);
      setNavAvatarMenuVisibility(false);
    }
  };

  const handleNavBarOptionClick = async (option: Route | null) => {
    if (!option) {
      return;
    }
    const navbarDropDownOptions = upNavbarDropdownOptions.get(
      option.navbarOptionId!,
    );

    if (navbarDropDownOptions && navbarDropDownOptions.length > 0) {
      const isActiveNavDropdownOption =
        activeNavBarOption.value?.navbarOptionId === option.navbarOptionId;

      if (isActiveNavDropdownOption && isNavDropdownVisible.value) {
        setNavDropdownVisibility(false);
        return;
      }

      if (option.navbarOptionId != null) {
        setNavBarDropdownOptions(option.navbarOptionId);
        setActiveNavBarOption(option.navbarOptionId);
        setNavDropdownVisibility(true);
      }
      return;
    }

    await navigateTo(option.path, { external: option.isExternalRoute });
    setNavDropdownVisibility(false);
  };

  const handleNavBarDropdownOptionClick = async (option: Route | null) => {
    if (
      activeRoute.value?.path === upNavRoutePaths.monitorSearch.path &&
      option?.path?.includes("/reports")
    ) {
      $bus.$emit("leaveRoute", {
        navPath: option.path,
        external: option.isExternalRoute,
      });
      return;
    }

    setActiveNavBarDropdownOption(option?.navbarDropdownOptionId ?? NaN);
    setNavDropdownVisibility(false);
    await navigateTo(option?.path, {
      external: option?.isExternalRoute,
    });
  };

  const resetNavigationState = () => {
    activeSidebarOption.value = null;
    activeNavBarOption.value = null;
    activeNavBarDropdownOption.value = null;
    activeTabOption.value = null;
    navBarOptions.value = undefined;
    navBarDropdownOptions.value = undefined;
  };

  // Normalizes dynamic route paths if any, returns the original route path otherwise
  const normalizeRoutePath = (routePath: string) => {
    // Normalizes Reports dynamic route paths
    if (!upNavRoutesByPath.has(routePath) && routePath.includes("reports#")) {
      return routePath.replace(/\/[^/]+\/edit$/, "/:id/edit");
    }

    return routePath;
  };

  const handleRouteRedirections = (routePath: string) => {
    if (!routePath || routePath === upNavRoutePaths.root.path) {
      return navigateTo(upNavRoutePaths.monitorSearch);
    }

    const normalizedRoutePath = normalizeRoutePath(routePath);
    const matchedRoute = upNavRoutesByPath.get(normalizedRoutePath);

    if (matchedRoute) {
      // Monitor Redirections

      if (
        matchedRoute.sidebarOptionName === "Monitor" &&
        !hasMonitorApplicationAccess.value &&
        routePath !== matchedRoute.upsellPath
      ) {
        return navigateTo(matchedRoute.upsellPath);
      }

      if (
        matchedRoute.path === upNavRoutePaths.monitorSocial.path &&
        !orgStore.hasSocialMediaAccess
      ) {
        return navigateTo(upNavRoutePaths.root.path);
      }

      // Contact Redirections

      if (
        matchedRoute.sidebarOptionName === "Contact" &&
        hasContactApplicationAccess.value &&
        routePath === matchedRoute.upsellPath
      ) {
        return navigateTo(upNavRoutePaths.root.path);
      }

      if (
        matchedRoute.sidebarOptionName === "Contact" &&
        !hasContactApplicationAccess.value &&
        routePath !== matchedRoute.upsellPath
      ) {
        return navigateTo(matchedRoute.upsellPath);
      }
    }

    return undefined;
  };

  const setInitialNavigationState = (routePath: string) => {
    // Resets
    resetNavigationState();

    // Hides navbar for upsell routes
    if (routePath.includes("/upsell")) {
      setNavbarVisibility(false);
    } else {
      setNavbarVisibility(true);
    }

    const normalizedRoutePath = normalizeRoutePath(routePath);
    const matchedRoute = upNavRoutesByPath.get(normalizedRoutePath);

    if (matchedRoute) {
      setActiveRoute(matchedRoute);

      if (matchedRoute.sidebarOptionName) {
        setActiveSideBarOption(matchedRoute.sidebarOptionName);
        setNavBarOptions(matchedRoute.sidebarOptionName);
      }
      if (matchedRoute.navbarOptionId) {
        setActiveNavBarOption(matchedRoute.navbarOptionId);
      }
      if (matchedRoute.navbarDropdownOptionId) {
        setActiveNavBarDropdownOption(matchedRoute.navbarDropdownOptionId);
      }
      if (matchedRoute.tabOptionId) {
        setActiveTabOption(matchedRoute.tabOptionId);
      }
    }
  };

  // Restores active navbar option when dropdown closes
  watch(isNavDropdownVisible, (isVisible) => {
    if (!isVisible && activeRoute.value?.navbarOptionId) {
      setActiveNavBarOption(activeRoute.value.navbarOptionId);
    }
  });

  return {
    // State
    upNavFlag,
    isNavbarVisible,
    isUpNavFlagLoading,
    isUpNavFlagReady,
    activeRoute,
    navBarOptions,
    isNavAvatarMenuVisible,
    isNavDropdownVisible,
    activeNavBarOption,
    activeSidebarOption,
    activeTabOption,
    navBarDropdownOptions,
    activeNavBarDropdownOption,
    newAnalyticsFlag,
    isNewAnalyticsFlagReady,
    isNewAnalyticsFlagLoading,

    // Getters
    hasMonitorApplicationAccess,
    hasContactApplicationAccess,
    avatarMenuOptions,
    avatarMenuTimeZone,
    navSidebarOptions: visibleNavSidebarOptions,
    isSidebarOptionPremiumBadgeVisible,
    isAnalyticsVisible,
    isAvatarMenuOptionActive,

    // Actions
    fetchUpNavFlag,
    fetchNewAnalyticsFlag,
    setNavbarVisibility,
    setActiveRoute,
    setNavBarOptions,
    setNavDropdownVisibility,
    setNavAvatarMenuVisibility,
    toggleNavAvatarMenuVisibility,
    handleAvatarMenuOptionClick,
    setActiveNavBarOption,
    handleNavBarOptionClick,
    setActiveSideBarOption,
    handleSidebarOptionClick,
    resetNavigationState,
    setInitialNavigationState,
    setActiveNavBarDropdownOption,
    handleNavBarDropdownOptionClick,
    handleRouteRedirections,
    hasDropdownOptions,
  };
});
